<script setup lang="ts">
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';

import {
  SEARCH_HINT_LABEL_METRO,
  SEARCH_HINT_LABEL_SUGGESTIONS,
} from '@/shared/constants/searchHintsConstants';

import type {
  SearchHintsProps,
  SearchHintValue,
  SearchHintMetro,
} from '@/features/SearchHints/model/types/searchHints';

import { searchHintLinkHandlers } from '@/features/SearchHints/lib/searchHintsUtil';

const props = defineProps<SearchHintsProps>();

const markText = (content: string): string => {
  if (!props.query || props.query === '') return content;
  return content.replace(
    new RegExp(props.query, 'gi'),
    (match) => `<span class="mark">${match}</span>`,
  );
};

const getName = (hint: SearchHintValue): string => {
  if (typeof hint === 'string') {
    return markText(hint);
  }

  if ('surname' in hint) {
    return markText(`${hint.surname} ${hint.name} ${hint.middlename ?? ''}`.trim());
  }

  return markText(hint.name);
};

const setLink = (hint: SearchHintValue, label: string): string => {
  if (typeof hint === 'string') {
    if (label === SEARCH_HINT_LABEL_SUGGESTIONS) {
      return `${ROOT_CATALOG_URL}?q=${hint}`;
    }
    return '';
  }

  const handler = searchHintLinkHandlers[label];

  if (!handler) {
    return '';
  }

  return handler(hint);
};

const isMetroHint = (hint: SearchHintValue): hint is SearchHintMetro =>
  typeof hint !== 'string' && 'color' in hint;
</script>

<template>
  <ul class="search-hints-list">
    <li
      v-for="item in items"
      :key="item.id"
      class="search-hints-list__item"
      data-testid="hints-group"
    >
      <div class="search-hints-list__label" data-testid="hints-label">
        {{ item.label }}
      </div>
      <ul class="search-hints-list__hints">
        <li
          v-for="hint in item.items"
          :key="typeof hint === 'string' ? hint : hint.id"
          class="search-hints-list__hint"
        >
          <NuxtLink class="search-hints-list__link _hint" :to="setLink(hint, item.label)">
            <span
              v-if="item.label === SEARCH_HINT_LABEL_METRO && isMetroHint(hint)"
              class="search-hints-list__color"
              data-testid="hints-metro-color"
              :style="{ backgroundColor: hint.color }"
            ></span>
            <span v-html="getName(hint)"></span>
          </NuxtLink>
        </li>
      </ul>
    </li>
  </ul>
</template>

<style lang="less">
.search-hints-list {
  padding: @size-x4 0;
  display: flex;
  flex-direction: column;

  &__item {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.3px;
    color: @color-manatee;
    width: 100%;

    &:not(:last-child) {
      margin-bottom: @size-x6;
    }

    &._selected {
      font-weight: 700;
    }
  }

  &__label {
    font-size: @size-x3;
    line-height: 18px;
    font-weight: 500;
    color: @color-dove-gray;
    padding: 0 30px 0 @size-x5;
  }

  &__hint {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 21px;
    font-weight: 500;
    margin-bottom: 6px;
  }

  &__color {
    display: inline-block;
    width: @size-x2;
    height: @size-x2;
    border-radius: 100%;
    margin-right: 5px;
  }

  &__link {
    width: 100%;
    padding: 0 30px 0 @size-x5;

    &:hover {
      background-color: #e2e2e2;
      box-shadow: 0px @size-x2 @size-x6 rgba(129, 135, 189, 0.15);
    }
  }
}
</style>
